import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { QcIssueCategory } from '../../../interfaces/qcIssue.interface';
import {
  applyMention,
  getMentionMatches,
  getMentionTerm,
  OrderedQuestionEntry,
} from './workspaceDerivations';
import QuestionMentionMenu from './QuestionMentionMenu';
import QcCategorySelect from './QcCategorySelect';
import { CloseButton, ModalLede } from './workspaceModalParts';
import { ACCENT_BLUE, ACCENT_BLUE_DARK } from './qcTheme';

// Composer chrome per the prototype's Add QC Issue dialog: 1.25rem title, 14px header padding, 6px-radius footer buttons.
const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '22px 24px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

const Title = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const Body = styled('div')({
  padding: '26px 24px 8px',
});

const FieldWrap = styled('div')({
  position: 'relative',
});

const FieldLabel = styled('span')({
  position: 'absolute',
  top: '-7px',
  left: '11px',
  background: '#fff',
  padding: '0 5px',
  fontSize: '0.72rem',
  color: ACCENT_BLUE,
  lineHeight: 1,
  pointerEvents: 'none',
  zIndex: 1,
});

const Draft = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '120px',
  border: `1.5px solid ${ACCENT_BLUE}`,
  borderRadius: '6px',
  padding: '12px 14px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  '&:focus': {
    boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
  },
});

const CategoryRow = styled('div')({
  marginTop: '22px',
});

const Footer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '20px 24px 22px',
});

const CancelButton = styled('button')({
  background: '#fff',
  color: 'rgba(0,0,0,0.7)',
  border: 'none',
  borderRadius: '6px',
  padding: '11px 18px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: 'rgba(0,0,0,0.04)',
  },
});

const AddButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '11px 18px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  boxShadow: '0 1px 3px rgba(25,118,210,0.4)',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.4)',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
});

export interface AddQcIssueModalProps {
  open: boolean;
  questionCode: string;
  entries: OrderedQuestionEntry[];
  onAdd: (category: QcIssueCategory, text: string) => void;
  onClose: () => void;
}

export default function AddQcIssueModal({ open, questionCode, entries, onAdd, onClose }: AddQcIssueModalProps): JSX.Element {
  const [text, setText] = useState('');
  const [category, setCategory] = useState<QcIssueCategory>('missing_evidence');
  const [mentionDismissed, setMentionDismissed] = useState(false);

  const mentionTerm = mentionDismissed ? null : getMentionTerm(text);
  const mentionMatches = mentionTerm === null ? [] : getMentionMatches(entries, mentionTerm, 6);

  const reset = () => {
    setText('');
    setCategory('missing_evidence');
    setMentionDismissed(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAdd = () => {
    if (!text.trim()) return;
    onAdd(category, text.trim());
    reset();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Add QC issue'
      PaperProps={{ sx: { width: 660, maxWidth: '100%', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <Header>
        <div>
          <Title>Add QC Issue</Title>
          <ModalLede>
            Create an issue for the assessor to address on {questionCode}. Every QC issue must be
            approved by TPN Admin before QC can be approved.
          </ModalLede>
        </div>
        <CloseButton type='button' aria-label='Close add QC issue' onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </Header>
      <Body>
        <FieldWrap>
          <FieldLabel>Comment to Assessor</FieldLabel>
          <QuestionMentionMenu
            matches={mentionMatches}
            floating
            onPick={shortCode => {
              setText(prev => applyMention(prev, shortCode));
              setMentionDismissed(false);
            }}
          />
          <Draft
            aria-label='Comment to Assessor'
            placeholder="Describe the issue with the assessor's response. Type # to link a question."
            value={text}
            onChange={event => { setText(event.target.value); setMentionDismissed(false); }}
            onKeyDown={event => { if (event.key === 'Escape') setMentionDismissed(true); }}
          />
        </FieldWrap>
        <CategoryRow>
          <QcCategorySelect value={category} onChange={setCategory} />
        </CategoryRow>
      </Body>
      <Footer>
        <CancelButton type='button' onClick={handleClose}>Cancel</CancelButton>
        <AddButton type='button' disabled={!text.trim()} onClick={handleAdd}>
          <AddCommentIcon sx={{ fontSize: 16 }} />
          Add Issue
        </AddButton>
      </Footer>
    </Dialog>
  );
}
